/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  LayerSpecification,
  type Map as MaplibreMap,
  StyleSpecification
} from "maplibre-gl";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry
} from "geojson";

import {
  COLOR_KEYS,
  COLOR_LABEL,
  COLOR_SCALE_MAP,
  COLORS
} from "@/constants/colors";
import { DEFAULT_GEOM_PRECISION } from "@/constants/map";
import { processNumericRange } from "@/helpers/numberHelpers";
import { BOUNDS_PADDING } from "@/hooks/map/interactions/useMapInteractions";
import { SpatialAggregationType } from "@/types/common";
import {
  ColorRanges,
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetStyle,
  EColorLabel,
  EColorNames
} from "@/types/dataset";
import { FeatureDataConfig, MapStyleProperties } from "@/types/map";

import * as turf from "@turf/turf";

export type LayerSublayers = {
  fill?: string;
  outline?: string;
  circle?: string;
  count?: string;
  unclustered?: string;
  line?: string;
  stroke?: string;
  label?: string;
  rank?: string;
};

/**
 * Creates HTML tooltip content for cluster features
 */
export const createClusterTooltip = (
  pointCount: number,
  layer:
    | { paint?: { "circle-radius": number | Array<string | number> } }
    | undefined
) => {
  const paint = layer?.paint;
  const clusterRadius = paint?.["circle-radius"];
  let radiusValue = 20;

  if (Array.isArray(clusterRadius) && clusterRadius[0] === "step") {
    const steps = clusterRadius.slice(2);
    for (let i = 0; i < steps.length; i += 2) {
      const threshold = Number(steps[i]);
      const radius = Number(steps[i + 1]);
      if (i + 1 < steps.length && pointCount >= threshold) {
        radiusValue = radius;
      }
    }
  }

  return `
        <div class="tooltip-content cluster-tooltip">
          <div class="tooltip-row">
            <span class="tooltip-label">Points in cluster:</span>
            <span class="tooltip-value">${pointCount}</span>
          </div>
          <div class="tooltip-row">
            <span class="tooltip-label">Cluster radius:</span>
            <span class="tooltip-value">${radiusValue}px</span>
          </div>
          <div class="tooltip-hint">Click to expand cluster</div>
        </div>
      `;
};

/**
 * Creates HTML tooltip content for individual point features
 */
export const createFeatureTooltip = (properties: Record<string, unknown>) => {
  const excludedKeys = [
    "point_count",
    "point_count_abbreviated",
    "cluster",
    "color",
    "opacity",
    "width"
  ];

  const tooltipContent = Object.entries(properties)
    .filter(([key]) => !excludedKeys.includes(key))
    .map(([key, value]) => {
      const formattedKey = key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return `
          <div class="tooltip-row">
            <span class="tooltip-label">${formattedKey}:</span>
            <span class="tooltip-value">${value}</span>
          </div>
          `;
    })
    .join("");

  return tooltipContent
    ? `<div class="tooltip-content">${tooltipContent}</div>`
    : "";
};

/**
 * Toggles the visibility of a layer and its sublayers
 */
// TODO: Tech debt might duplicate with @handleChangeVisibility src/utils/map.ts:84
// TODO: Tech debt currently time complexity is O(n) need to improve to O(1) possible  using `getLayer`
// TODO: Problem in site_profiling when toogle the visibility marker show/hide first before the layer
export const toggleLayerVisibilityById = (
  map: MaplibreMap | null,
  datasetId: string,
  isVisible: boolean,
  showLabel: boolean,
  isRankEnabled: boolean
): void => {
  if (!map) return;
  const layers = map.getStyle().layers;

  layers.forEach((layer) => {
    if (!("source" in layer) || !layer.source.startsWith(datasetId)) return;

    const isLabel = layer.id.endsWith("-label");
    const isRank = layer.id.endsWith("-rank");

    let visibility: "visible" | "none" = "none";
    if (!isLabel && !isRank) {
      visibility = isVisible ? "visible" : "none";
    } else if (isLabel) {
      visibility = showLabel && isVisible ? "visible" : "none";
    } else if (isRank) {
      visibility = isRankEnabled && isVisible ? "visible" : "none";
    }

    map.setLayoutProperty(layer.id, "visibility", visibility);
  });
};

/**
 * Generates a Maplibre "match" expression for coloring `ordinal` categories/features.
 *
 * @param colorRanges - Array of color range objects, each containing a value to match and its color.
 * @param colorFamily - The color family key to use from the `COLORS` constant.
 * @param category - The feature property name to match for coloring.
 * @returns `ExpressionSpecification` for Maplibre layer color styling, mapping each category value to its color.
 */
export function generateOrdinalColorExpression(
  colorRanges: ColorRanges[],
  colorFamily: EColorNames,
  category: string,
  step: number
): ExpressionSpecification {
  const scale = COLOR_SCALE_MAP[step];
  return [
    "match",
    ["get", category],
    colorRanges[0]?.value ?? "",
    // Color for the first value in colorRanges
    COLORS[colorFamily][scale[0]],
    // Color for each remaining value in colorRanges
    ...(colorRanges.length
      ? colorRanges
          .slice(1)
          .flatMap((range, index) => [
            range.value ? range.value : "",
            range.value ? COLORS[colorFamily][scale[index + 1]] : "#CECECE"
          ])
      : [" ", COLORS[colorFamily][scale[scale.length - 1]]]),
    // Fallback color if no match is found
    COLORS[colorFamily][scale.at(-1)!]
  ];
}

export const addPolygonLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  style?: MapStyleProperties | undefined
): void => {
  if (!map) return;

  map.addLayer({
    id: `${sourceId}-fill`,
    type: "fill",
    source: sourceId,
    layout: {},
    paint: {
      "fill-color": style?.fillColor ?? "rgba(136, 136, 136, 0.4)",
      "fill-opacity": [
        "case",
        [
          "any",
          ["boolean", ["feature-state", "hover"], false],
          ["boolean", ["feature-state", "selected"], false]
        ],
        0.6, // Hover opacity
        style?.fillOpacity || 0.9 // Default opacity
      ]
    }
  });
};

export const addPolygonOutlineLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  style?: MapStyleProperties | undefined
): void => {
  if (!map) return;

  map.addLayer({
    id: `${sourceId}-stroke`,
    type: "line",
    source: sourceId,
    layout: {},
    paint: {
      "line-color": style?.lineColor ?? "#088C7A",
      "line-width": style?.lineWidth ?? 2,
      "line-dasharray": [4, 4],
      "line-opacity": [
        "case",
        [
          "any",
          ["boolean", ["feature-state", "hover"], false],
          ["boolean", ["feature-state", "selected"], false]
        ],
        1,
        style?.opacity ?? 1
      ]
    }
  });
};

export const addRoutingLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  style?: MapStyleProperties | undefined
): void => {
  if (!map) return;
  map.addLayer({
    id: `${sourceId}-fill-routing`,
    type: "fill",
    source: sourceId, // Assuming your source is a polygon
    paint: {
      "fill-color": style?.fillColor ?? "rgba(75, 197, 9, 0.2)"
    }
  });
  map.addLayer({
    id: `${sourceId}-line`,
    type: "line",
    source: sourceId,
    paint: {
      "line-color": style?.lineColor ?? "rgba(75, 197, 9, 1)", // Customize line color
      "line-width": style?.lineWidth ?? 2 // Customize line width
    }
  });
};

export const addCircleRadiusLayer = (
  map: MaplibreMap | null,
  sourceId: string,
  style?: MapStyleProperties | undefined
): void => {
  if (!map) return;
  map.addLayer({
    id: `${sourceId}-fill-circle-radius`,
    type: "fill",
    source: sourceId,
    paint: {
      "fill-color": style?.fillColor ?? "rgba(75, 197, 9, 0.4)",
      "fill-opacity": style?.fillOpacity ?? 0.4
    }
  });
  map.addLayer({
    id: `${sourceId}-line`,
    type: "line",
    source: sourceId,
    paint: {
      "line-color": style?.lineColor ?? "rgba(75, 197, 9, 1)", // Customize line color
      "line-width": style?.lineWidth ?? 2 // Customize line width
    }
  });
};

export const removeRegionPolygonSource = (
  map: MaplibreMap | null,
  datasetId: string
): void => {
  if (!map) return;
  if (map.getLayer(`${datasetId}-fill`)) map.removeLayer(`${datasetId}-fill`);
  if (map.getLayer(`${datasetId}-line`)) map.removeLayer(`${datasetId}-line`);
  if (map.getSource(datasetId)) map.removeSource(datasetId);
};

export const changeColorOrdinalLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  colorFamily: EColorNames,
  colorRanges: ColorRanges[],
  selectedCategory: string,
  step: number
) => {
  if (!map) return;

  // Create ordinal color expression using match
  const ordinalColorExpression = generateOrdinalColorExpression(
    colorRanges,
    colorFamily,
    selectedCategory,
    step
  );

  const colorExpression = [
    "case",
    ["has", selectedCategory],
    ordinalColorExpression,
    "#CECECE"
  ];

  // Handle polygon layers
  if (sublayers.fill && map.getLayer(sublayers.fill)) {
    map.setPaintProperty(sublayers.fill, "fill-color", colorExpression);
  }

  // Handle point layers
  if (
    sublayers.circle &&
    sublayers.unclustered &&
    map.getLayer(sublayers.circle) &&
    map.getLayer(sublayers.unclustered)
  ) {
    // Update clustered points
    map.setPaintProperty(
      sublayers.circle,
      "circle-color",
      COLORS[colorFamily][COLOR_SCALE_MAP[step][0]]
    );

    // Update unclustered points
    map.setPaintProperty(
      sublayers.unclustered,
      "circle-color",
      colorExpression
    );
  }

  // Handle line layers
  if (sublayers.stroke && map.getLayer(sublayers.stroke)) {
    map.setPaintProperty(sublayers.stroke, "line-color", colorExpression);
  }
};

export const changeColorQuantileLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  colorFamily: EColorNames,
  colorRanges: DatasetStyle["colorRanges"],
  selectedCategory: string,
  step: number
) => {
  if (!map) return;

  // Handle case when colorRanges has only one item
  if (colorRanges.length === 1) {
    const singleColor =
      COLORS[colorFamily][COLOR_KEYS[0]] || colorRanges[0]?.color;

    const colorExpression = [
      "case",
      ["==", ["get", selectedCategory], null],
      "#CECECE",
      ["has", selectedCategory],
      singleColor,
      "#CECECE" // Default fallback color
    ];

    // Handle polygon layers
    if (sublayers.fill && map.getLayer(sublayers.fill)) {
      map.setPaintProperty(sublayers.fill, "fill-color", colorExpression);
    }

    // Handle point layers
    if (
      sublayers.circle &&
      sublayers.unclustered &&
      map.getLayer(sublayers.circle) &&
      map.getLayer(sublayers.unclustered)
    ) {
      // Update clustered points
      map.setPaintProperty(
        sublayers.circle,
        "circle-color",
        COLORS[colorFamily][COLOR_SCALE_MAP[step][0]] || colorRanges[0]?.color
      );

      // Update unclustered points
      map.setPaintProperty(
        sublayers.unclustered,
        "circle-color",
        colorExpression
      );
    }

    return;
  }

  // Create an array of unique breakpoints and their colors
  const uniqueStops = colorRanges
    .filter((range) => range.min != null) // skip null min values
    .reduce((acc: [number, string][], range, index) => {
      if (!acc.some(([value]) => value === range.min)) {
        acc.push([
          range.min!,
          COLORS[colorFamily][COLOR_SCALE_MAP[step][index]]
        ]);
      }
      return acc;
    }, [])
    .sort(([a], [b]) => a - b);

  // Handle case when uniqueStops has less than 2 entries (not enough for a valid step expression)
  if (uniqueStops.length < 2) {
    // If no ranges or just one range, use a default or the single color
    const fillColor =
      uniqueStops.length === 1
        ? uniqueStops[0][1]
        : COLORS[colorFamily][COLOR_SCALE_MAP[step][0]];

    const colorExpression = [
      "case",
      ["has", selectedCategory],
      fillColor,
      "#CECECE" // Default fallback color
    ];

    // Handle polygon layers
    if (sublayers.fill && map.getLayer(sublayers.fill)) {
      map.setPaintProperty(sublayers.fill, "fill-color", colorExpression);
    }

    // Handle point layers
    if (
      sublayers.circle &&
      sublayers.unclustered &&
      map.getLayer(sublayers.circle) &&
      map.getLayer(sublayers.unclustered)
    ) {
      // Update clustered points
      map.setPaintProperty(sublayers.circle, "circle-color", colorExpression);

      // Update unclustered points
      map.setPaintProperty(
        sublayers.unclustered,
        "circle-color",
        colorExpression
      );
    }

    return;
  }

  // We have at least 2 entries, so we can create a valid step expression
  const stepExpression = [
    "step",
    ["get", selectedCategory],
    uniqueStops[0][1], // Default color for values below first threshold
    uniqueStops[1][0],
    uniqueStops[1][1], // First threshold and color
    ...uniqueStops.slice(2).flatMap(([value, color]) => [value, color])
  ];

  const colorExpression = [
    "case",
    ["==", ["get", selectedCategory], null],
    "#CECECE",
    ["has", selectedCategory],
    stepExpression,
    "#CECECE"
  ];

  // Handle polygon layers
  if (sublayers.fill && map.getLayer(sublayers.fill)) {
    map.setPaintProperty(sublayers.fill, "fill-color", colorExpression);
  }

  // Handle point layers
  if (
    sublayers.circle &&
    sublayers.unclustered &&
    map.getLayer(sublayers.circle) &&
    map.getLayer(sublayers.unclustered)
  ) {
    // Update clustered points
    map.setPaintProperty(
      sublayers.circle,
      "circle-color",
      COLORS[colorFamily][COLOR_SCALE_MAP[step][0]] || colorRanges[0]?.color
    );

    // Update unclustered points
    map.setPaintProperty(
      sublayers.unclustered,
      "circle-color",
      colorExpression
    );
  }

  // Handle line layers
  if (sublayers.stroke && map.getLayer(sublayers.stroke)) {
    map.setPaintProperty(sublayers.stroke, "line-color", colorExpression);
  }
};

export const changeOpacityLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  opacity: number
) => {
  if (!map) return;

  // Handle polygon layers
  if (
    sublayers.fill &&
    sublayers.outline &&
    map.getLayer(sublayers.fill) &&
    map.getLayer(sublayers.outline)
  ) {
    map.setPaintProperty(sublayers.fill, "fill-opacity", opacity);
    map.setPaintProperty(sublayers.outline, "line-opacity", opacity);
  }

  // Handle point layers
  if (
    sublayers.circle &&
    sublayers.unclustered &&
    map.getLayer(sublayers.circle) &&
    map.getLayer(sublayers.unclustered)
  ) {
    // Update clustered points
    map.setPaintProperty(sublayers.circle, "circle-opacity", opacity);
    map.setPaintProperty(sublayers.circle, "circle-stroke-opacity", opacity);

    // Update unclustered points
    map.setPaintProperty(sublayers.unclustered, "circle-opacity", opacity);
    map.setPaintProperty(
      sublayers.unclustered,
      "circle-stroke-opacity",
      opacity
    );
  }

  // Handle line layers
  if (sublayers.stroke && map.getLayer(sublayers.stroke)) {
    map.setPaintProperty(sublayers.stroke, "line-opacity", opacity);
  }
};

export const changeStrokeWidthLayer = (
  map: MaplibreMap | null,
  sublayers: LayerSublayers,
  strokeWidth: number
) => {
  if (!map) return;

  if (sublayers.stroke && map.getLayer(sublayers.stroke)) {
    map.setPaintProperty(sublayers.stroke, "line-width", strokeWidth);
  }
};

export const removeAllSourceLayerWithPrefix = async (
  map: MaplibreMap | null,
  prefix: string,
  ignoreTextList: string[]
): Promise<void> => {
  if (!map) return;
  const layerIdsToRemove = map
    ?.getStyle()
    ?.layers?.filter((layer) => layer.id.startsWith(prefix))
    ?.map((layer) => layer.id);

  layerIdsToRemove?.forEach((layerId) => {
    map.removeLayer(layerId);
  });
  // Get all source start with passing prefix
  const sources = Object.keys(map?.getStyle()?.sources ?? {})?.filter(
    (source) => source.includes(prefix)
  );
  // Filter source ignore text
  const sourceIgnoreText = sources.filter(
    (source) => !ignoreTextList.some((text) => source.includes(text))
  );
  sourceIgnoreText.forEach((source) => {
    if (map.getSource(source)) map.removeSource(source);
  });
};

export const getSourceProperties = (
  map: MaplibreMap | null,
  sourceId: string
) => {
  if (!map) return;

  // We use startsWith() to match source IDs because in @useSiteProfilingLayers.ts we append suffixes
  // like '-polygon' to create custom layer IDs. This ensures we find the base source ID regardless
  // of any suffixes that might have been added.
  const matchingSourceId = Object.keys(map.getStyle().sources).find(
    (sourceKey) => sourceKey.startsWith(sourceId)
  );

  if (!matchingSourceId) return;

  const source = map.getSource(matchingSourceId);

  if (!source) return;

  const data = (source as maplibregl.GeoJSONSource)._data;
  if (typeof data === "string" || !data || !("features" in data)) return [];

  return data;
};

export const addLayer = ({
  isNewUIDatasetConfigEnabled,
  isRankingLabelEnabled,
  map,
  layerData,
  style,
  selectedCategory,
  isOrdinal,
  addOrdinalFunc,
  addQuantileFunc
}: {
  isNewUIDatasetConfigEnabled: boolean;
  isRankingLabelEnabled: boolean;
  map: MaplibreMap;
  layerData: Omit<FeatureDataConfig, "data"> & { id: string };
  style: DatasetStyle;
  selectedCategory: string;
  isOrdinal: boolean;
  addOrdinalFunc: (
    isNewUIDatasetConfigEnabled: boolean,
    isRankingLabelEnabled: boolean,
    map: MaplibreMap,
    layer: Omit<FeatureDataConfig, "data"> & { id: string },
    style: DatasetStyle,
    category: string
  ) => void;
  addQuantileFunc: (
    isNewUIDatasetConfigEnabled: boolean,
    isRankingLabelEnabled: boolean,
    map: MaplibreMap,
    dataset: Omit<FeatureDataConfig, "data"> & { id: string },
    style: DatasetStyle,
    category: string
  ) => void;
}) => {
  const addLayerMethods = {
    ordinal: () =>
      addOrdinalFunc(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        layerData,
        style,
        selectedCategory
      ),
    quantile: () =>
      addQuantileFunc(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        layerData,
        style,
        selectedCategory
      )
  };

  const method = isOrdinal ? "ordinal" : "quantile";
  addLayerMethods[method]();
};

export const getRadiusPolygon = (
  center: [number, number],
  radius: number
): Feature<Geometry, GeoJsonProperties> => {
  const circle = turf.circle(center, radius, {
    steps: 64,
    units: "meters"
  });
  return circle;
};

export const hideAllLayersWithPrefixes = (
  map: MaplibreMap,
  prefixes: string[]
) => {
  const hiddenLayerIds: string[] = [];

  map.getStyle().layers.forEach((layer) => {
    if (prefixes.some((prefix) => layer.id.startsWith(prefix))) {
      const visibility = map.getLayoutProperty(layer.id, "visibility");
      if (visibility !== "none") {
        map.setLayoutProperty(layer.id, "visibility", "none");
        hiddenLayerIds.push(layer.id);
      }
    }
  });
  return hiddenLayerIds;
};

export const showLayersWithLayerIds = (
  map: MaplibreMap,
  layerIds: string[]
) => {
  const combinedFeatures: Feature<Geometry, GeoJsonProperties>[] = [];

  layerIds.forEach((layerId) => {
    map.setLayoutProperty(layerId, "visibility", "visible");
    const layer = map.getLayer(layerId);
    if (layer?.source) {
      const source = map.getSource(layer.source);
      if (source && (source as any).type === "geojson") {
        const geojsonSource = source as {
          _data?: FeatureCollection<Geometry, GeoJsonProperties>;
          _options?: { data?: FeatureCollection<Geometry, GeoJsonProperties> };
        };
        const data = geojsonSource._data || geojsonSource._options?.data;
        if (data && Array.isArray(data.features)) {
          combinedFeatures.push(...data.features);
        }
      }
    }
  });

  if (combinedFeatures.length > 0) {
    const geojson: FeatureCollection<Geometry, GeoJsonProperties> = {
      type: "FeatureCollection",
      features: combinedFeatures
    };
    const bounds = turf.bbox(geojson); // [minX, minY, maxX, maxY]
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]]
      ],
      {
        padding: BOUNDS_PADDING
      }
    );
  }
};

export const reorderLayerBasedOnArea = (
  features: FeatureCollection<Geometry>
): FeatureCollection<Geometry> => {
  const sortedFeatures = [...features.features].sort((a, b) => {
    const areaA = turf.area(a);
    const areaB = turf.area(b);
    return areaB - areaA;
  });

  return {
    ...features,
    features: sortedFeatures
  };
};

/**
 * Gets the ID of the item that appears immediately before the target in a given list.
 *
 * @param targetId - The ID of the target item.
 * @param orders - Optional array of objects containing an `id` and a `type`.
 * @returns A string with the ID and layer type (`circle` for "poi", `fill` otherwise), or `undefined` if not found.
 */
export function getBeforeId(
  targetId: string,
  orders?: { id: string; type: SpatialAggregationType }[]
): string | undefined {
  if (!orders?.length) return undefined;

  const list = orders.map((v) => v.id);
  const index = list.indexOf(targetId);

  if (index === -1 || index === 0) return undefined;

  const previousOrder = orders[index - 1];

  const getLayerSuffix = (type: SpatialAggregationType): string => {
    if (type === "poi") return "circle";
    if (type === "line") return "stroke";
    return "fill";
  };

  return `${previousOrder.id}-${getLayerSuffix(previousOrder.type)}`;
}

/**
 * Filters out features whose property values match any of the provided filters.
 *
 * @param features - Array of GeoJSON features.
 * @param field - The property key to filter by.
 * @param filters - Filter conditions (categorical or numerical).
 * @param dataType - The type of data (float, int, string, etc.).
 * @param top9 - An array of top 9 most common string values used for special comparison logic.
 * @returns An array of features that do NOT match any of the filters.
 */
export function filterPointFeatures(
  features: Feature[],
  field: string,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined,
  dataType: DataSchemaType,
  top9: string[]
): Feature[] {
  if (!filters?.length) return features;

  return features.filter(({ properties }) => {
    const value = properties?.[field];
    if (value === null || value === undefined) return true;
    return !isFeatureMatchingFilters(value, filters, dataType, top9);
  });
}

/**
 * Generates a Mapbox GL filter expression to exclude features based on filter conditions.
 *
 * @param field - The property key to filter by.
 * @param filters - Filter conditions (categorical or numerical).
 * @param dataType - The type of data (float, int, string, etc.).
 * @param top9 - An array of top 9 most common string values used for special comparison logic.
 * @returns A Mapbox GL filter expression or `null` if no filters apply.
 */
export function generateNonPointMapFilter(
  field: string,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined,
  dataType: DataSchemaType,
  top9: string[]
) {
  if (!filters?.length) return null;

  if ([DataSchemaType.FLOAT, DataSchemaType.INT].includes(dataType)) {
    const rangeFilters = (filters as { min: number; max: number }[]).map(
      (range) => {
        // handle null & 0 value
        if (!range.min && !range.max) {
          return [
            "==",
            ["get", field],
            typeof range.min === "number" ? 0 : null
          ];
        }
        const { min: processedMin, max: processedMax } =
          processNumericRange(range);

        return [
          "all",
          ["!=", ["get", field], null],
          [">=", ["get", field], processedMin],
          ["<=", ["get", field], processedMax]
        ];
      }
    );

    return ["!", ["any", ...rangeFilters]];
  }

  const exclusionFilters = (filters as string[]).map((v) =>
    top9.includes(v)
      ? ["!=", ["get", field], v]
      : ["!", ["match", ["get", field], top9, false, true]]
  );

  return exclusionFilters.length ? ["all", ...exclusionFilters] : null;
}

/**
 * Determines whether a feature's property value matches any of the provided filters.
 *
 * @param value - The value to test.
 * @param filters - Filter conditions (categorical or numerical).
 * @param dataType - The type of data (FLOAT, INT, STRING, etc.).
 * @param top9Values - An array of top 9 values used for special matching logic.
 * @returns `true` if the value matches any filter, `false` otherwise.
 */
export function isFeatureMatchingFilters(
  value: string | number,
  filters:
    | (string | { min: number | null; max: number | null } | null)[]
    | undefined,
  dataType: DataSchemaType,
  top9Values: string[]
): boolean {
  if (!filters?.length) return false;

  if ([DataSchemaType.FLOAT, DataSchemaType.INT].includes(dataType)) {
    if (typeof value === "number") {
      return filters.some((f) => {
        if (typeof f === "object" && f !== null) {
          const { min: processedMin, max: processedMax } =
            processNumericRange(f);
          return value >= processedMin && value <= processedMax;
        }
        return false;
      });
    } else if (!value) {
      return filters.some((f) => f === "null");
    }
  } else if (typeof value === "boolean") {
    let boolValue: string;
    if (value === true) {
      boolValue = "true";
    } else if (value === false) {
      boolValue = "false";
    } else {
      boolValue = "null";
    }
    return filters.includes(boolValue);
  }

  return filters.some((f) => {
    if (typeof f !== "string") return false;
    if (f === "") return value === "";
    return top9Values.includes(f)
      ? value === f
      : !top9Values.includes(value as string) && value !== ""; // this is for "others"
  });
}

/**
 * Truncate a GeoJSON Feature (Point or Polygon) to fixed decimal precision.
 *
 * Supports: Point, Polygon
 *
 * @param feature - GeoJSON Feature<Geometry, GeoJsonProperties>
 * @param precision - Decimal precision (default: 6)
 * @param coordinateDimensions - Number of coordinate dimensions (default: 2)
 * @param mutate - Whether to mutate the input geometry (default: false)
 * @returns Truncated GeoJSON Feature<Geometry, GeoJsonProperties>
 */
export const truncateGeoJson = <T extends Geometry>(
  feature: Feature<T, GeoJsonProperties>,
  precision = DEFAULT_GEOM_PRECISION,
  coordinateDimensions = 2,
  mutate = false
): Feature<T, GeoJsonProperties> => {
  if (!feature?.geometry || !("coordinates" in feature.geometry)) {
    return feature;
  }

  const truncatedFeature = turf.truncate(feature, {
    precision,
    coordinates: coordinateDimensions,
    mutate
  });

  return {
    ...feature,
    geometry: truncatedFeature.geometry as T
  };
};

export const transformStyle = (
  previousStyle?: StyleSpecification,
  nextStyle?: StyleSpecification
): StyleSpecification => {
  // Projection from mapbox causing error
  delete nextStyle?.projection;

  if (!previousStyle && !nextStyle) {
    throw new Error("transformStyle received no styles to merge");
  }

  if (!nextStyle) return previousStyle!;
  if (!previousStyle) return nextStyle;

  const prev = previousStyle;
  const next = nextStyle;

  const isCartoId = (id: string) => id.toLowerCase().includes("carto");
  const isCompositeId = (id: string) => id.toLowerCase().includes("composite");
  const isMapboxDraw = (id: string) => id.includes("mapbox-gl-draw");
  const isMapboxSatellite = (id: string) =>
    id.toLowerCase().includes("mapbox-satellite");

  const filteredPreviousSources = Object.fromEntries(
    Object.entries(prev.sources ?? {}).filter(
      ([id]) =>
        !isCartoId(id) &&
        !isCompositeId(id) &&
        !isMapboxDraw(id) &&
        !isMapboxSatellite(id)
    )
  );

  const mergedSources = {
    ...filteredPreviousSources,
    ...Object.fromEntries(
      Object.entries(next.sources ?? {}).map(([id, source]) => [id, source])
    )
  };

  const processedNextLayers: LayerSpecification[] = (next.layers ?? []).map(
    (layer) => layer
  );

  const mergedLayers = [
    ...processedNextLayers,
    ...(prev.layers ?? []).filter((layer) => {
      if (layer.type === "background") return false;

      if (
        layer?.source === "carto" ||
        layer?.source === "composite" ||
        layer?.source.includes("mapbox-gl-draw") ||
        layer?.source === "mapbox-satellite"
      )
        return false;

      return true;
    })
  ];

  return {
    ...next,
    sources: mergedSources,
    layers: mergedLayers
  };
};

export const changeLabelLayerVisibility = (
  map: MaplibreMap | null,
  layerId: string,
  visibility: boolean
) => {
  if (!map) return;

  const style = map.getStyle();
  if (!style.layers) return;

  // Ensure the label layer exists
  const labelLayer = style.layers.find((l) => l.id === layerId);
  if (!labelLayer) return;

  // Update label layer visibility
  map.setLayoutProperty(layerId, "visibility", visibility ? "visible" : "none");

  // Calculate the corresponding offset for the rank layer
  const labelRankLayerId = layerId.replace("label", "rank");
  const rankOffset = calculateRankOffset(map, layerId, visibility);

  // Update rank layer offset
  const rankLayer = style.layers.find((l) => l.id === labelRankLayerId);
  if (rankLayer) {
    map.setLayoutProperty(labelRankLayerId, "text-offset", rankOffset);
  }
};

export const changeCategoryLabelLayer = (
  map: MaplibreMap | null,
  layerId: string,
  category?: DatasetAttributeSchema
) => {
  if (!map) return;

  const style = map.getStyle();

  if (!style.layers) return;

  map.setLayoutProperty(layerId, "text-field", [
    "to-string",
    ["get", category?.attribute_name]
  ]);
};

export const changeLabelLayerColor = (
  map: MaplibreMap | null,
  layerId: string,
  selectedColor: EColorLabel
) => {
  if (!map) return;

  const style = map.getStyle();
  if (!style.layers) return;

  const colorLabel = COLOR_LABEL[selectedColor];

  map.setPaintProperty(layerId, "text-color", colorLabel.COLOR);
  map.setPaintProperty(layerId, "text-halo-color", colorLabel.HALO_COLOR);
};

export const changeLabelLayerOpacity = (
  map: MaplibreMap | null,
  layerId: string,
  opacity: number
) => {
  if (!map) return;

  const style = map.getStyle();
  if (!style.layers) return;

  map.setPaintProperty(layerId, "text-opacity", opacity);
};

export const calculateRankOffset = (
  map: MaplibreMap,
  labelLayerId: string,
  isLabelVisible: boolean
): [number, number] => {
  const labelOffset = map.getLayoutProperty(labelLayerId, "text-offset");
  const baseOffsetY = labelOffset?.[1] ?? 0;
  const nextOffsetY = isLabelVisible ? baseOffsetY - 2 : baseOffsetY;
  return [0, nextOffsetY];
};

export const getTextSizeExpression = (
  isPOI: boolean
): DataDrivenPropertyValueSpecification<number> => [
  "interpolate",
  ["linear"],
  ["zoom"],
  13,
  9, // zoom 13 → 9px
  14,
  12, // zoom 14 → 12px
  ...(isPOI ? [] : [16, 16]) // zoom 16 → 16px for polygon
];

export const getLabelOffset = (isPOI: boolean, isSiteProfiling: boolean) => {
  const base = isPOI ? -1 : 0;
  return isSiteProfiling ? base + 3 : base;
};

export const getRankOffset = (
  showLabel: boolean,
  isPOI: boolean,
  isSiteProfiling: boolean
) => {
  const baseWithLabel = isPOI ? -3 : -2;
  const baseWithoutLabel = isPOI ? -1 : 0;

  const base = showLabel ? baseWithLabel : baseWithoutLabel;

  return isSiteProfiling ? base + 3 : base;
};
